import { useEffect } from 'react'
import { StatusBadge } from '@/components/ui/Badge'
import { Field, Input, Select, Textarea } from '@/components/ui/Field'
import { ReviewDrawer } from '@/components/review/ReviewDrawer'
import { useEntityDraft } from '@/components/review/useEntityDraft'
import type { FactKind, PartnerFact } from '@/types'

interface Draft {
  kind: FactKind
  label: string
  value: string
  sourceUrl: string
  tags: string[]
}

const DEFAULTS: Draft = {
  kind: 'additional',
  label: '',
  value: '',
  sourceUrl: '',
  tags: [],
}

export function FactDrawer({
  fact,
  tagPool,
  onClose,
  onSave,
  onReview,
  onFavorite,
}: {
  fact: PartnerFact | 'new' | null
  tagPool: string[]
  onClose: () => void
  onSave: (patch: Partial<PartnerFact> & { id?: string }) => Promise<void>
  onReview: (id: string, status: 'approved' | 'rejected') => void
  onFavorite: (id: string, favorite: boolean) => void
}) {
  const creating = fact === 'new'
  const existing = creating ? null : fact

  const draft = useEntityDraft<Draft>(
    existing
      ? {
          kind: existing.kind,
          label: existing.label,
          value: existing.value,
          sourceUrl: existing.sourceUrl ?? '',
          tags: existing.tags,
        }
      : null,
    DEFAULTS,
  )

  const { reset } = draft
  useEffect(() => {
    if (!fact) reset()
  }, [fact, reset])

  if (!fact) return null

  function close() {
    draft.reset()
    onClose()
  }

  return (
    <ReviewDrawer
      open
      onClose={close}
      title={creating ? 'Add fact' : draft.value.label || 'Partner fact'}
      subtitle={
        existing && (
          <span className="flex items-center gap-2">
            <StatusBadge status={existing.status} />
            <span className="capitalize text-ink-faint">{existing.kind} fact</span>
          </span>
        )
      }
      creating={creating}
      entity={existing}
      saveLabel={creating ? 'Add fact' : 'Save changes'}
      onSave={() =>
        void onSave({
          ...(existing ? { id: existing.id } : {}),
          kind: draft.value.kind,
          label: draft.value.label,
          value: draft.value.value,
          sourceUrl: draft.value.sourceUrl || null,
          tags: draft.value.tags,
        })
      }
      onApprove={() => existing && onReview(existing.id, 'approved')}
      onReject={() => existing && onReview(existing.id, 'rejected')}
      onFavorite={(next) => existing && onFavorite(existing.id, next)}
      tags={draft.value.tags}
      tagPool={tagPool}
      onTagsChange={(tags) => draft.set('tags', tags)}
    >
      <Field label="Section" hint="Standard facts follow the fixed template; additional facts are LLM-identified.">
        {(p) => (
          <Select
            {...p}
            value={draft.value.kind}
            onChange={(e) => draft.set('kind', e.target.value as FactKind)}
          >
            <option value="standard">Standard fact</option>
            <option value="additional">Additional fact</option>
          </Select>
        )}
      </Field>

      <Field label="Label" required>
        {(p) => (
          <Input {...p} value={draft.value.label} onChange={(e) => draft.set('label', e.target.value)} />
        )}
      </Field>

      <Field label="Value" required hint="Write it as the agent should quote it, including units and the as-of date.">
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={draft.value.value}
            onChange={(e) => draft.set('value', e.target.value)}
          />
        )}
      </Field>

      <Field label="Source page">
        {(p) => (
          <Input
            {...p}
            value={draft.value.sourceUrl}
            className="font-mono text-[13px]"
            onChange={(e) => draft.set('sourceUrl', e.target.value)}
          />
        )}
      </Field>
    </ReviewDrawer>
  )
}
